import type { RefObject } from 'react';
import { useEffect } from 'react';

import { identifyUser, setUserProperties } from '@/modules/analytics';
import { getNativeAppClientInfo } from '@/modules/clientContext';
import type { User } from '@/types/user';

import type { AuthStatus } from '../types/authStatus';

type UseAnalyticsIdentifyArgs = {
  authStatus: AuthStatus;
  userData: User | undefined;
  orgParam: string | undefined;
  /**
   * Owned by RulesEngineProvider (cleared in `resetUserData` so a new user can
   * re-identify after logout / 401), passed in here so this hook stays a pure
   * side-effect layer over the provider's state.
   */
  hasIdentifiedRef: RefObject<boolean>;
};

/**
 * Fires the analytics identify (once per session) + the load-derived person-
 * property backfill. Extracted verbatim from RulesEngineProvider — see the
 * inline comments for the Sparta parallels and ordering rationale.
 */
export function useAnalyticsIdentify({
  authStatus,
  userData,
  orgParam,
  hasIdentifiedRef,
}: UseAnalyticsIdentifyArgs): void {
  // Identify the user to analytics exactly once per session, the moment they
  // become known. This single chokepoint covers both a fresh signup (createUser
  // → refreshUser) and a resuming user (bootstrap → refreshUser). Anonymous
  // users are never identified; the ref guard prevents re-identifying on later
  // user-object updates, and resetUserData clears it so a new user can identify
  // after logout / 401.
  //
  // The properties known at signup mirror Sparta's createUserSideEffects
  // identify; the load-derived ones (paying-org / tier / transition) are set by
  // the backfill effect below — mirroring Sparta's richer loadCurrentUserEpic
  // identify. `vida_client` / `vida_client_version` are registered app-wide as
  // PostHog super-properties at analytics init; `is_view_embedded_in_native_app`
  // is the per-person flag Sparta's loadCurrentUserEpic sets, derived here from
  // the captured launch context.
  useEffect(() => {
    if (hasIdentifiedRef.current) return;
    if (authStatus !== 'authenticated' || !userData) return;
    hasIdentifiedRef.current = true;
    identifyUser(userData);
    setUserProperties({
      isSsoUser: Boolean(userData.customerProfile.ssoPayingOrganization),
      teamCode: userData.customerProfile.soloTeamCode,
      isCampaignUser: Boolean(orgParam),
      isViewEmbeddedInNativeApp: getNativeAppClientInfo().isLoadedInNativeApp,
    });
  }, [authStatus, userData, orgParam, hasIdentifiedRef]);

  // Backfill the load-derived person properties (Sparta sets these in
  // loadCurrentUserEpic, not at create-user). The identify above runs once and,
  // for a fresh signup, fires BEFORE eligibility — so paying-org / tier /
  // transition are empty there. Re-set them whenever `userData` changes after
  // identify (a passing eligibility check populates payingOrganization; a tier
  // assignment sets activeTierName), so PostHog reflects them in-session instead
  // of only on the next refresh. Idempotent — re-sets the same values for a
  // resuming user. Gated on the identify having run so it never attaches to the
  // anonymous user.
  useEffect(() => {
    if (!hasIdentifiedRef.current || authStatus !== 'authenticated' || !userData) return;
    const orgUrn =
      userData.payingOrganization?.urn ??
      userData.customerProfile.ssoPayingOrganization?.urn ??
      null;
    const orgName =
      userData.payingOrganization?.name ??
      userData.customerProfile.ssoPayingOrganization?.name ??
      null;
    setUserProperties({
      payingOrgUrn: orgUrn,
      payingOrgName: orgName,
      isTransitionMember: Boolean(userData.customerProfile.isTransitionMember),
      activeHighestTier: userData.activeTierName ?? null,
    });
  }, [authStatus, userData, hasIdentifiedRef]);
}
